import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { saveDraft, queuePhoto, getQueuedPhotos, removeQueuedPhoto } from '../utils/offlineDb';
import SignaturePad from '../components/SignaturePad';
import PlanView from '../components/PlanView';
import { useAuth } from '../store/auth';
import { useSite } from '../store/site';
import { useNavigate } from 'react-router-dom';

const JOURS    = ['J1','J2','J3','J4','J5','J6','J7','J8','J9','J10','J11','J12',
                  'J13','J14','J15','J16','J17','J18','J19','J20','J21','J22','J23',
                  'J24','J25','J26','J27','J28','J29','J30','J31','J32','J33','J34'];
const JLABELS  = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi'];
const INCIDENTS_NATURE = ['Panne matériel','Problème accès zone','Câble non conforme',
  'Problème controller/réseau','Incident sécurité','AP endommagé','Nacelle en panne','Autre'];
const IMPACTS = ['Aucun impact','Retard < 1h','Retard 1h-3h','Retard > 3h','AP reporté J+1'];
const PRIORITES = ['Haute','Moyenne','Basse'];

function initPresences(names) {
  return (names || []).map(name => ({
    techName:name, present:true, heureArrivee:'', heureDepart:'', pauseDuree:'', heuresProductives:'', observation:''
  }));
}
function initMateriaux(catalog) {
  return (catalog || []).map(m => ({ designation:m.designation, quantite:'', unite:m.unite, isCustom:false }))
    .concat(Array(5).fill(null).map(() => ({ designation:'', quantite:'', unite:'', isCustom:true })));
}

const TYPE_COLOR = { NEW:'#1F4E79', REUTIL:'#2E7D32', AVERIFIER:'#EF6C00' };
const TYPE_LABEL = { NEW:'NEW', REUTIL:'RÉUTIL', AVERIFIER:'À VÉR.' };

const OFFICE_ROLES = ['BUREAU', 'ADMIN'];

export default function Rapport() {
  const { user, logout } = useAuth();
  const { site, setSite } = useSite();
  const nav = useNavigate();
  const [step, setStep] = useState(0); // 0=en-tête, 1=presences, 2=ap, 3=zones, 4=materiaux, 5=incidents, 6=restants
  const [localId, setLocalId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apList, setApList] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [semaines, setSemaines] = useState(['S1']);
  const [catalog, setCatalog] = useState([]);
  const [teamNames, setTeamNames] = useState([]);

  // Form state
  const [header, setHeader] = useState({ semaine:'S1', numJour:'J1', jour:'Lundi',
    date: new Date().toISOString().slice(0,10), conditions:[], objTirage:'', objPose:'', objNote:'' });
  const [presences, setPresences]   = useState([]);
  const [apItems, setApItems]       = useState([]);
  const [workZones, setWorkZones]   = useState([]);
  const [materiaux, setMateriaux]   = useState([]);
  const [incidents, setIncidents]   = useState([]);
  const [restants, setRestants]     = useState([{ tache:'', quantite:'', priorite:'Haute', note:'' }]);
  const [pendingRestants, setPendingRestants] = useState([]); // restants ouverts J-1
  const [editingReportId, setEditingReportId] = useState(null); // reprise/édition d'un rapport
  const [pendingReportAps, setPendingReportAps] = useState(null); // items à réappliquer après fetchAP
  const [rejectInfo, setRejectInfo] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signataire, setSignataire] = useState('');
  const [queueCount, setQueueCount] = useState(0);
  const [plannedNight, setPlannedNight] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPlan, setShowPlan] = useState(null); // {plan, status, annotations}
  const [annotateMode, setAnnotateMode] = useState(null);
  const [annotateColor, setAnnotateColor] = useState('#C62828');
  const [weekProg, setWeekProg] = useState(null);

  // Chargement de la config du site : semaines, équipe, catalogue matériaux
  useEffect(() => {
    if (!site) return;
    api.get(`/sites/${site.id}`).then(({ data }) => {
      setSemaines(data.semaines?.length ? data.semaines : ['S1']);
      setHeader(h => ({ ...h, semaine: data.semaines?.[0] || 'S1' }));
      const names = data.users.map(u => u.user)
        .filter(u => ['TECH','CHEF'].includes(u.role)).map(u => u.name);
      setTeamNames(names);
      setPresences(p => p.length ? p : initPresences(names));
    }).catch(() => {});
    api.get(`/sites/${site.id}/materiaux`).then(({ data }) => {
      setCatalog(data);
      setMateriaux(m => m.length ? m : initMateriaux(data));
    }).catch(() => {});
    api.get(`/sites/${site.id}/restants?open=1`).then(({ data }) => setPendingRestants(data)).catch(() => {});
    api.get(`/sites/${site.id}/nights`).then(({ data }) => {
      const today = new Date().toISOString().slice(0, 10);
      const night = data.find(n => n.date.slice(0, 10) === today)
        || data.find(n => n.date.slice(0, 10) > today && n.realise == null);
      if (night) setPlannedNight(night);
    }).catch(() => {});
    api.get(`/sites/${site.id}/plans`).then(({ data }) => setPlans(data)).catch(() => {});
    // Rapport rejeté à reprendre ?
    api.get(`/reports?siteId=${site.id}&status=DRAFT`).then(({ data }) => {
      const rej = data.find(r => r.rejectReason);
      if (rej) setRejectInfo({ id: rej.id, semaine: rej.semaine, numJour: rej.numJour, reason: rej.rejectReason });
    }).catch(() => {});
    // eslint-disable-next-line
  }, [site?.id]);

  // Pont pour StepZones (mise en file depuis le sous-composant)
  useEffect(() => {
    window.__queuePhotoZone = async (file, zoneIdx) => {
      const qid = await queuePhoto(file, file.name, { kind:'zone', key: zoneIdx });
      setQueueCount(c => c + 1);
      return qid;
    };
    return () => { delete window.__queuePhotoZone; };
  }, []);

  // ── File de photos hors-ligne : flush au retour en ligne ──────────
  async function flushPhotoQueue() {
    const queued = await getQueuedPhotos();
    setQueueCount(queued.length);
    if (!navigator.onLine || !queued.length) return;
    for (const q of queued) {
      const fd = new FormData();
      fd.append('photo', q.blob, q.name || 'photo.jpg');
      try {
        const { data } = await api.post('/reports/photo', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        // Remplacer l'entrée locale par la photo serveur dans le bon slot
        if (q.target.kind === 'ap') {
          setApItems(a => a.map(it => it.apRef === q.target.key
            ? { ...it, photos: (it.photos || []).map(ph => ph.qid === q.qid ? data : ph) } : it));
        } else {
          setWorkZones(z => z.map((wz, i) => i === q.target.key
            ? { ...wz, photos: (wz.photos || []).map(ph => ph.qid === q.qid ? data : ph) } : wz));
        }
        await removeQueuedPhoto(q.qid);
      } catch { /* on retentera */ }
    }
    setQueueCount((await getQueuedPhotos()).length);
  }
  useEffect(() => {
    flushPhotoQueue();
    const onUp = () => flushPhotoQueue();
    window.addEventListener('online', onUp);
    return () => window.removeEventListener('online', onUp);
    // eslint-disable-next-line
  }, []);

  // ── Édition bureau/admin : /rapport?edit=<id> ──
  const [editParamDone, setEditParamDone] = useState(false);
  useEffect(() => {
    const editId = new URLSearchParams(window.location.search).get('edit');
    if (!editId || editParamDone) return;
    setEditParamDone(true);
    setDraftRestored(true); // ne pas proposer le brouillon par-dessus
    (async () => {
      try {
        const { data: r } = await api.get(`/reports/${editId}`);
        if (!site || site.id !== r.siteId)
          setSite({ id: r.siteId, nom: r.site?.nom || 'Chantier' });
        await applyReport(r);
      } catch (e) { alert(e.response?.data?.error || 'Rapport inaccessible'); }
    })();
    // eslint-disable-next-line
  }, []);

  // ── Reprendre le rapport rejeté : recharge l'état depuis le serveur ──
  async function resumeRejected() {
    const { data: r } = await api.get(`/reports/${rejectInfo.id}`);
    await applyReport(r);
  }
  async function applyReport(r) {
    setEditingReportId(r.id);
    setHeader({ semaine: r.semaine, numJour: r.numJour, jour: r.jour,
      date: r.date.slice(0, 10), conditions: r.conditions || [],
      objTirage: r.objTirage || '', objPose: r.objPose || '', objNote: r.objNote || '' });
    if (r.presences?.length) setPresences(r.presences.map(({ id, reportId, ...x }) => x));
    if (r.apItems?.length) setPendingReportAps(r.apItems);
    if (r.workZones?.length) setWorkZones(r.workZones.map(z => ({
      description: z.description, travaux: z.travaux || '', heureDebut: z.heureDebut || '',
      heureFin: z.heureFin || '', photos: z.photos || [] })));
    if (r.materiaux?.length) setMateriaux(m => m.map(x => {
      const found = r.materiaux.find(y => y.designation === x.designation);
      return found ? { ...x, quantite: String(found.quantite ?? '') } : x;
    }));
    if (r.incidents?.length) setIncidents(r.incidents.map(({ id, reportId, photos, ...x }) => x));
    if (r.restants?.length) setRestants(r.restants.map(({ id, reportId, done, doneAt, ...x }) => x));
  }

  function applyPlannedNight() {
    setHeader(h => ({ ...h,
      date: plannedNight.date.slice(0, 10),
      semaine: plannedNight.semaine, numJour: plannedNight.numJour,
      objPose: plannedNight.objPose != null ? `${plannedNight.objPose} AP à poser` : h.objPose,
      objTirage: plannedNight.objTirage != null ? `${plannedNight.objTirage} tirages` : h.objTirage,
      objNote: plannedNight.note ? (h.objNote ? h.objNote + '\n' : '') + plannedNight.note : h.objNote,
    }));
    setPlannedNight(null);
  }

  async function openPlan(plan) {
    try {
      const [st, an] = await Promise.all([
        api.get(`/sites/${site.id}/plans/${plan.id}/status`),
        api.get(`/sites/${site.id}/plans/${plan.id}/annotations`),
      ]);
      setShowPlan({ plan, status: st.data, annotations: an.data });
    } catch { setShowPlan({ plan, status: [], annotations: [] }); }
    setAnnotateMode(null);
  }
  async function addDrawing(points) {
    const { data } = await api.post(`/sites/${site.id}/plans/${showPlan.plan.id}/annotations`,
      { kind: 'draw', color: annotateColor, data: { points } });
    setShowPlan(sp => ({ ...sp, annotations: [...sp.annotations, data] }));
  }
  async function addText(x, y, text) {
    const { data } = await api.post(`/sites/${site.id}/plans/${showPlan.plan.id}/annotations`,
      { kind: 'text', color: annotateColor, data: { x, y, text } });
    setShowPlan(sp => ({ ...sp, annotations: [...sp.annotations, data] }));
  }
  async function delAnnotation(a) {
    try {
      await api.delete(`/sites/${site.id}/plans/${showPlan.plan.id}/annotations/${a.id}`);
      setShowPlan(sp => ({ ...sp, annotations: sp.annotations.filter(x => x.id !== a.id) }));
    } catch (e) { alert(e.response?.data?.error || 'Suppression impossible'); }
  }

  // AP terminé : câble (tiré OU réutilisé) + posé + contrôlé — saisie OU état connu
  const isDone = (it) => {
    const k = it.known || {};
    const cable = it.cableTire === 'Oui' || it.cableReutil === 'Oui'
      || k.cableTire === 'Oui' || k.cableReutil === 'Oui';
    const pose = it.apPose === 'Oui' || k.apPose === 'Oui';
    const ping = !!it.controllerPing || !!k.controllerPing;
    return cable && pose && ping;
  };
  // AP traité : au moins câble ou pose (saisie ou connu)
  const isTreated = (it) => {
    const k = it.known || {};
    return it.cableTire !== 'Non' || it.cableReutil !== 'Non' || it.apPose !== 'Non'
      || !!it.controllerPing || k.cableTire === 'Oui' || k.cableReutil === 'Oui'
      || k.apPose === 'Oui' || k.apPose === 'En cours' || k.cableTire === 'En cours';
  };


  // Reprendre les restants J-1 comme note d'objectif
  function importPendingRestants() {
    const txt = pendingRestants.map(r =>
      `- ${r.tache}${r.quantite ? ' (' + r.quantite + ')' : ''} [${r.priorite || 'Moyenne'}]`).join('\n');
    setHeader(h => ({ ...h, objNote: (h.objNote ? h.objNote + '\n' : '') + 'Restants J-1 :\n' + txt }));
  }

  useEffect(() => {
    const up = () => setOnline(true);
    const dn = () => setOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', dn);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', dn); };
  }, []);

  useEffect(() => { fetchAP(); /* eslint-disable-next-line */ }, [site?.id]);
  useEffect(() => {
    if (!pendingReportAps || !apItems.length) return;
    setApItems(prev => prev.map(it => {
      const found = pendingReportAps.find(x => x.apRef === it.apRef);
      return found ? { ...it, cableTire: found.cableTire || 'Non',
        cableReutil: found.cableReutil || 'Non', apPose: found.apPose || 'Non',
        controllerPing: !!found.controllerPing,
        observation: found.observation || '', photos: found.photos || [] } : it;
    }));
    setPendingReportAps(null);
    // eslint-disable-next-line
  }, [apItems.length, pendingReportAps]);
  useEffect(() => { loadWeekProgress(); /* eslint-disable-next-line */ }, [header.semaine, site?.id]);

  async function loadWeekProgress() {
    if (!site) return;
    api.get(`/dashboard/week-progress?siteId=${site.id}&semaine=${header.semaine}`)
      .then(({ data }) => setWeekProg(data)).catch(() => setWeekProg(null));
  }

  async function fetchAP() {
    if (!site) return;
    try {
      // Tous les AP du site, avec leur dernier état connu (rapports soumis)
      const { data } = await api.get(`/sites/${site.id}/ap/status`);
      setApList(data);
      // Merge atomique : la liste serveur fait référence, la saisie en cours
      // (ou restaurée d'un brouillon) est préservée par apRef.
      setApItems(prev => {
        const byRef = Object.fromEntries(prev.map(a => [a.apRef, a]));
        return data.map(ap => {
          const cur = byRef[ap.ref];
          return cur
            ? { ...cur, known: ap.known || null, semaine: ap.semaine,
                zone: ap.zone, type: ap.type, modele: ap.modele }
            : { apRef: ap.ref, zone: ap.zone, semaine: ap.semaine, type: ap.type,
                modele: ap.modele, known: ap.known || null,
                cableTire:'Non', cableReutil:'Non', apPose:'Non',
                controllerPing:false, observation:'', photos:[] };
        });
      });
    } catch {}
  }

  async function autoSave() {
    const draft = { header, presences, apItems, workZones, materiaux, incidents, restants, localId };
    const id = await saveDraft(draft);
    setLocalId(id);
    // Copie serveur : permet de reprendre le brouillon sur un autre appareil
    if (navigator.onLine && site) {
      api.put('/reports/draft', { siteId: site.id, payload: draft }).catch(() => {});
    }
  }

  // Reprise du brouillon serveur au chargement (si présent)
  const [draftRestored, setDraftRestored] = useState(false);
  useEffect(() => {
    if (!site || draftRestored) return;
    if (new URLSearchParams(window.location.search).get('edit')) return; // mode édition bureau
    api.get(`/reports/draft?siteId=${site.id}`).then(({ data }) => {
      if (!data?.payload) return;
      const d = data.payload;
      if (window.confirm('Un brouillon en cours existe pour ce chantier. Le reprendre ?')) {
        if (d.header)    setHeader(d.header);
        if (d.presences?.length) setPresences(d.presences);
        if (d.apItems?.length)   setApItems(d.apItems);
        fetchAP(); // recharge la liste complète du site en préservant la saisie restaurée
        if (d.workZones) setWorkZones(d.workZones);
        if (d.materiaux?.length) setMateriaux(d.materiaux);
        if (d.incidents) setIncidents(d.incidents);
        if (d.restants?.length)  setRestants(d.restants);
      }
      setDraftRestored(true);
    }).catch(() => setDraftRestored(true));
    // eslint-disable-next-line
  }, [site?.id]);

  async function submitReport() {
    if (queueCount > 0) {
      await flushPhotoQueue();
      const left = (await getQueuedPhotos()).length;
      if (left > 0) { alert(`${left} photo(s) en attente d'envoi — repassez en ligne avant de soumettre.`); return; }
    }
    setSubmitting(true);
    try {
      let rid = editingReportId;
      if (rid) {
        await api.put(`/reports/${rid}`, { ...header, date: new Date(header.date).toISOString() });
      } else {
        const chefEquipe = await api.get('/auth/me');
        const { data: report } = await api.post('/reports', {
          ...header, siteId: site.id, date: new Date(header.date).toISOString(),
          chefEquipeId: chefEquipe.data.id
        });
        rid = report.id;
      }
      const apPayload = apItems
        .filter(a => a.cableTire !== 'Non' || a.cableReutil !== 'Non' || a.apPose !== 'Non' || a.controllerPing)
        .map(({ known, ...a }) => ({ ...a,
          horsSemaine: !!(a.semaine && a.semaine !== header.semaine),
          photoIds: (a.photos || []).filter(p => p.id).map(p => p.id) }));
      const zonePayload = workZones.map((z, i) => ({ ...z, ordre: i,
        photoIds: (z.photos || []).filter(p => p.id).map(p => p.id) }));
      await api.put(`/reports/${rid}/presences`, { presences });
      await api.put(`/reports/${rid}/ap`, { items: apPayload });
      await api.put(`/reports/${rid}/workzones`, { zones: zonePayload });
      await api.put(`/reports/${rid}/materiaux`, { lines: materiaux.filter(m => m.quantite && parseFloat(m.quantite) > 0) });
      await api.put(`/reports/${rid}/incidents`, { incidents });
      await api.put(`/reports/${rid}/restants`, { restants: restants.filter(r => r.tache) });
      await api.post(`/reports/${rid}/submit`, { signataire, signatureData: signature });
      setEditingReportId(null); setRejectInfo(null); setSignature(null); setSignataire('');
      setSubmitted(true);
    } catch(e) {
      alert('Erreur soumission. Rapport sauvegardé en brouillon.');
      autoSave();
    } finally { setSubmitting(false); }
  }

  const STEPS = ['En-tête','Présences','Points d\'accès','Zones de travail','Matériaux','Incidents','Restants','Validation'];

  if (submitted) return (
    <div style={styles.center}>
      <div style={{fontSize:64}}>✅</div>
      <h2 style={{color:'#2E7D32',marginTop:16}}>Rapport soumis !</h2>
      <p style={{color:'#555',marginTop:8}}>Le rapport a été envoyé au bureau.</p>
      <button style={{...styles.btn, marginTop:24, background:'#1F4E79'}}
        onClick={() => { setSubmitted(false); setStep(0); setApItems([]);
          setWorkZones([]); setMateriaux(initMateriaux(catalog)); setIncidents([]);
          setRestants([{ tache:'', quantite:'', priorite:'Haute', note:'' }]);
          setPresences(initPresences(teamNames)); }}>
        Nouveau rapport
      </button>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Topbar */}
      <div style={styles.topbar} className="lw-band">
        <div style={{minWidth:0}}>
          <span style={styles.topbarTitle}>{site?.nom || 'LIDWORK'}</span>
          <p style={{fontSize:11, opacity:.75}}>{user?.name}</p>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <span style={{...styles.badge, background: online ? '#2E7D32' : '#C62828'}}>
            {online ? 'En ligne' : 'Hors ligne'}
          </span>
          {plans.length > 0 && (
            <button style={styles.topBtn} onClick={() => openPlan(plans[0])}>Plan</button>
          )}
          <button style={styles.topBtn} title="Changer de chantier"
            onClick={() => { setSite(null); nav('/select-site'); }}>⇄</button>
          <button style={styles.topBtn} onClick={logout}>Quitter</button>
        </div>
      </div>

      {rejectInfo && !editingReportId && (
        <div style={styles.rejectBanner}>
          <div>
            <b>Rapport {rejectInfo.semaine} {rejectInfo.numJour} rejeté par le bureau</b>
            <p style={{fontSize:13, marginTop:2}}>Motif : {rejectInfo.reason}</p>
          </div>
          <button style={styles.rejectBtn} onClick={resumeRejected}>Corriger</button>
        </div>
      )}
      {editingReportId && OFFICE_ROLES.includes(user?.role) && (
        <div style={{...styles.planBanner, borderLeftColor:'#8A6D00', background:'#FFFBEC', color:'#8A6D00'}}>
          <div><b>Mode édition — rapport {header.semaine} {header.numJour}</b>
            <p style={{fontSize:13, marginTop:2}}>Modifications bureau : la re-soumission régénérera le PDF.</p></div>
        </div>
      )}
      {plannedNight && !editingReportId && (
        <div style={styles.planBanner}>
          <div>
            <b>Nuit planifiée : {plannedNight.semaine} {plannedNight.numJour}</b>
            <p style={{fontSize:13, marginTop:2}}>
              {new Date(plannedNight.date).toLocaleDateString('fr-FR')}
              {plannedNight.objPose != null ? ` · objectif ${plannedNight.objPose} poses` : ''}
              {plannedNight.objTirage != null ? `, ${plannedNight.objTirage} tirages` : ''}
            </p>
          </div>
          <button style={styles.planBannerBtn} onClick={applyPlannedNight}>Pré-remplir</button>
        </div>
      )}
      {queueCount > 0 && (
        <div style={styles.queueBanner}>
          {queueCount} photo(s) en attente — envoi automatique au retour du réseau
        </div>
      )}

      {/* Balisage d'étapes */}
      <div style={styles.stepsWrap}>
        {STEPS.map((s,i) => (
          <button key={i} title={s}
            style={{...styles.stepSeg,
              background: i < step ? '#2E7D32' : i === step ? '#FFC91F' : '#DDE4F0'}}
            onClick={() => setStep(i)}/>
        ))}
      </div>
      <p style={styles.stepLabel}>Étape {step+1}/8 — {STEPS[step]}</p>

      {/* Content */}
      <div style={styles.content}>
        {step === 0 && <StepHeader header={header} setHeader={setHeader} semaines={semaines}
          pendingRestants={pendingRestants} onImportRestants={importPendingRestants} />}
        {step === 1 && <StepPresences presences={presences} setPresences={setPresences} />}
        {step === 2 && <StepAP apItems={apItems} setApItems={setApItems} apList={apList}
          isDone={isDone} isTreated={isTreated} weekProg={weekProg}
          currentSemaine={header.semaine} />}
        {step === 3 && <StepZones workZones={workZones} setWorkZones={setWorkZones} />}
        {step === 4 && <StepMateriaux materiaux={materiaux} setMateriaux={setMateriaux} />}
        {step === 5 && <StepIncidents incidents={incidents} setIncidents={setIncidents} />}
        {step === 6 && <StepRestants restants={restants} setRestants={setRestants} />}
        {step === 7 && <StepValidation header={header} apItems={apItems} presences={presences}
                          incidents={incidents} restants={restants} workZones={workZones}
                          signature={signature} setSignature={setSignature}
                          signataire={signataire} setSignataire={setSignataire} />}
      </div>

      {showPlan && (
        <div style={styles.modalWrap} onClick={() => setShowPlan(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
              <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                {plans.map(pl => (
                  <button key={pl.id} style={{...styles.chip,
                    ...(showPlan.plan.id===pl.id?styles.chipOn:{})}}
                    onClick={() => openPlan(pl)}>{pl.nom}</button>
                ))}
              </div>
              <button style={styles.topBtn2} onClick={() => setShowPlan(null)}>Fermer</button>
            </div>
            <div style={{display:'flex', gap:6, alignItems:'center', marginBottom:8, flexWrap:'wrap'}}>
              <button style={{...styles.chip, ...(annotateMode==='draw'?styles.chipOn:{})}}
                onClick={() => setAnnotateMode(m => m==='draw' ? null : 'draw')}>✏️ Dessiner</button>
              <button style={{...styles.chip, ...(annotateMode==='text'?styles.chipOn:{})}}
                onClick={() => setAnnotateMode(m => m==='text' ? null : 'text')}>💬 Texte</button>
              {['#C62828','#FFC91F','#2E75B6','#2E7D32'].map(c => (
                <button key={c} onClick={() => setAnnotateColor(c)}
                  style={{ width:22, height:22, borderRadius:'50%', background:c, padding:0,
                    border: annotateColor===c ? '3px solid #13264D' : '2px solid #fff',
                    boxShadow:'0 0 0 1px #DDE4F0' }}/>
              ))}
              {annotateMode && <span style={{fontSize:12, color:'#5A6478'}}>
                {annotateMode==='draw' ? 'Tracez au doigt sur le plan' : 'Touchez le plan pour placer un texte'}
              </span>}
            </div>
            <PlanView imagePath={showPlan.plan.imagePath} aps={showPlan.status}
              annotations={showPlan.annotations} annotateMode={annotateMode}
              annotateColor={annotateColor} onAddDrawing={addDrawing} onAddText={addText}/>
            {showPlan.annotations.length > 0 && (
              <div style={{marginTop:8}}>
                {showPlan.annotations.filter(a => a.authorId === user.id || user.role === 'ADMIN').map(a => (
                  <span key={a.id} style={{display:'inline-flex', alignItems:'center', gap:4,
                    fontSize:11, color:'#5A6478', marginRight:10}}>
                    <span style={{width:8, height:8, borderRadius:'50%', background:a.color}}/>
                    {a.kind==='text' ? `"${a.data.text.slice(0,20)}"` : 'tracé'} ({a.authorName})
                    <button style={{background:'none', color:'#C62828', fontSize:11,
                      textDecoration:'underline', padding:0}} onClick={() => delAnnotation(a)}>suppr.</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={styles.nav}>
        <button style={{...styles.btn, background:'#eee', color:'#333'}}
          onClick={() => { autoSave(); setStep(s => Math.max(0,s-1)); }}
          disabled={step===0}>◀ Précédent</button>
        <button style={{...styles.btn, background:'#595959', fontSize:13}}
          onClick={autoSave} disabled={saving}>💾 Sauvegarder</button>
        {step < STEPS.length-1
          ? <button style={styles.btn} onClick={() => { autoSave(); setStep(s=>s+1); }}>Suivant ▶</button>
          : <button style={{...styles.btn, background:'#2E7D32'}}
              onClick={submitReport} disabled={submitting}>
              {submitting ? '⏳ Envoi...' : '✅ Soumettre'}
            </button>
        }
      </div>
    </div>
  );
}

// ── Étape 0 : En-tête ─────────────────────────────────────────────────
function StepHeader({ header, setHeader, semaines, pendingRestants, onImportRestants }) {
  const upd = f => setHeader(h => ({ ...h, ...f }));
  function toggleCond(c) {
    setHeader(h => ({ ...h, conditions: h.conditions.includes(c)
      ? h.conditions.filter(x=>x!==c) : [...h.conditions, c] }));
  }
  const conds = ['Normal','Incident','Retard','Accès refusé','Matériel manquant','Problème matériel'];
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Informations de la nuit</h3>
      {pendingRestants?.length > 0 && (
        <div style={{...styles.card, borderLeft:'4px solid #FFC91F', background:'#FFFBEC'}}>
          <p style={{fontWeight:700, color:'#8A6D00', marginBottom:6}}>
            {pendingRestants.length} restant(s) des nuits précédentes
          </p>
          {pendingRestants.slice(0,5).map(r => (
            <p key={r.id} style={{fontSize:13, color:'#5A6478'}}>
              • {r.tache}{r.quantite ? ` (${r.quantite})` : ''} — {r.priorite || 'Moyenne'} · {r.report.semaine} {r.report.numJour}
            </p>
          ))}
          {pendingRestants.length > 5 && <p style={{fontSize:12,color:'#8A6D00'}}>… et {pendingRestants.length-5} autres</p>}
          <button style={{...styles.chip, marginTop:8, borderColor:'#FFC91F', color:'#8A6D00'}}
            onClick={onImportRestants}>Reprendre comme objectifs de la nuit</button>
        </div>
      )}
      <Row label="Semaine">
        <Select value={header.semaine} onChange={v=>upd({semaine:v})} options={semaines}/>
      </Row>
      <Row label="Numéro de jour">
        <Select value={header.numJour} onChange={v=>upd({numJour:v})} options={JOURS}/>
      </Row>
      <Row label="Jour">
        <Select value={header.jour} onChange={v=>upd({jour:v})} options={JLABELS}/>
      </Row>
      <Row label="Date">
        <input style={styles.input} type="date" value={header.date}
          onChange={e=>upd({date:e.target.value})}/>
      </Row>
      <div style={styles.card}>
        <p style={styles.cardLabel}>Conditions chantier</p>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          {conds.map(c => (
            <button key={c} style={{...styles.chip, ...(header.conditions.includes(c)?styles.chipOn:{})}}
              onClick={()=>toggleCond(c)}>{c}</button>
          ))}
        </div>
      </div>
      <div style={styles.card}>
        <p style={styles.cardLabel}>🔧 Objectif tirage câble</p>
        <textarea style={styles.textarea} rows={2} value={header.objTirage}
          onChange={e=>upd({objTirage:e.target.value})} placeholder="Ex: Bureaux NEW 22 prises..."/>
      </div>
      <div style={styles.card}>
        <p style={styles.cardLabel}>📡 Objectif pose AP</p>
        <textarea style={styles.textarea} rows={2} value={header.objPose}
          onChange={e=>upd({objPose:e.target.value})} placeholder="Ex: 32 AP bureaux + C1 new..."/>
      </div>
      <div style={styles.card}>
        <p style={styles.cardLabel}>⚠️ Note / Contrainte</p>
        <textarea style={styles.textarea} rows={2} value={header.objNote}
          onChange={e=>upd({objNote:e.target.value})} placeholder="Ex: Switch prioritaire. EPI obligatoires."/>
      </div>
    </div>
  );
}

// ── Étape 1 : Présences ────────────────────────────────────────────────
const toMinP = v => {
  if (!v || !/^\d{1,2}:\d{2}$/.test(String(v).trim())) return null;
  const [h, m] = String(v).trim().split(':').map(Number);
  return (h > 23 || m > 59) ? null : h * 60 + m;
};
function calcHeures(arr, dep, pause) {
  const a = toMinP(arr), d = toMinP(dep);
  if (a === null || d === null) return null;
  let end = d; if (end <= a) end += 1440;
  const brut = end - a, p = toMinP(pause) || 0;
  let nuit = 0;
  for (let t = a; t < end; t++) { const m = t % 1440; if (m >= 1260 || m < 420) nuit++; }
  const pN = brut ? Math.round(p * nuit / brut) : 0;
  const r1 = v => Math.round(Math.max(0, v) / 6) / 10;
  return { total: r1(brut - p), jour: r1(brut - nuit - (p - pN)), nuit: r1(nuit - pN) };
}

function StepPresences({ presences, setPresences }) {
  function upd(i, field, val) {
    setPresences(p => p.map((r,j) => {
      if (j !== i) return r;
      const next = { ...r, [field]: val };
      if (['heureArrivee','heureDepart','pauseDuree'].includes(field)) {
        const h = calcHeures(next.heureArrivee, next.heureDepart, next.pauseDuree);
        next.heuresProductives = h ? h.total : '';
      }
      return next;
    }));
  }
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Temps de travail</h3>
      {presences.map((p,i) => (
        <div key={i} style={{...styles.card, borderLeft:`4px solid ${p.present?'#2E7D32':'#ccc'}`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <span style={{fontWeight:600,fontSize:15}}>{p.techName}</span>
            <button style={{...styles.chip,...(p.present?styles.chipOn:{})}}
              onClick={()=>upd(i,'present',!p.present)}>
              {p.present ? '✅ Présent' : '❌ Absent'}
            </button>
          </div>
          {p.present && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <Field label="Arrivée" type="time" value={p.heureArrivee} onChange={v=>upd(i,'heureArrivee',v)}/>
              <Field label="Départ"  type="time" value={p.heureDepart}  onChange={v=>upd(i,'heureDepart',v)}/>
              <Field label="Pause"   placeholder="ex: 00:30" value={p.pauseDuree} onChange={v=>upd(i,'pauseDuree',v)}/>
              <div>
                <p style={{fontSize:12,color:'#666',marginBottom:4}}>Total (auto)</p>
                <div style={{...styles.input, background:'#F4F6FB', display:'flex',
                  alignItems:'center', justifyContent:'space-between'}}>
                  <b className="tnum">{p.heuresProductives !== '' && p.heuresProductives != null ? `${p.heuresProductives} h` : '—'}</b>
                  {(() => { const h = calcHeures(p.heureArrivee, p.heureDepart, p.pauseDuree);
                    return h && h.nuit > 0
                      ? <span style={{fontSize:11, color:'#5A6478'}}>dont {h.nuit} h nuit</span> : null; })()}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Étape 2 : Points d'accès ──────────────────────────────────────────
function ApCard({ item, i, upd, uploadPhoto, fileRefs, done, currentSemaine }) {
  const needPhoto = item.apPose === 'Oui' && (!item.photos || item.photos.length === 0);
  const k = item.known;
  const knownBits = [];
  if (k) {
    if (k.cableTire === 'Oui') knownBits.push('tiré');
    else if (k.cableTire === 'En cours') knownBits.push('tirage en cours');
    if (k.cableReutil === 'Oui') knownBits.push('réutilisé');
    if (k.apPose === 'Oui') knownBits.push('posé');
    else if (k.apPose === 'En cours') knownBits.push('pose en cours');
    if (k.controllerPing) knownBits.push('pingé');
  }
  return (
              <div key={item.apRef} style={{...styles.apCard,
                borderLeft:`4px solid ${done ? '#2E7D32' : (TYPE_COLOR[item.type]||'#ccc')}`,
                ...(needPhoto?{background:'#fff9f0'}:{}),
                ...(done?{background:'#F0F7F1', opacity:.92}:{})}}>
                <div style={styles.apHeader}>
                  <span style={{fontWeight:700,color: done ? '#2E7D32' : TYPE_COLOR[item.type]}}>{item.apRef}</span>
                  <span style={{display:'flex', gap:5}}>
                    {currentSemaine && item.semaine && item.semaine !== currentSemaine &&
                      <span style={{...styles.typeBadge, background:'#8A6D00'}}>{item.semaine}</span>}
                    {done && <span style={{...styles.typeBadge, background:'#2E7D32'}}>✓ Terminé</span>}
                    <span style={{...styles.typeBadge,background:TYPE_COLOR[item.type]}}>
                      {TYPE_LABEL[item.type]}
                    </span>
                  </span>
                </div>
                {knownBits.length > 0 && (
                  <p style={{fontSize:12, color:'#5A6478', margin:'2px 0 6px'}}>
                    Déjà : {knownBits.join(' · ')}{k?.report ? ` (${k.report.semaine} ${k.report.numJour})` : ''}
                  </p>
                )}
                <p style={{fontSize:12,color:'#888',marginBottom:10}}>{item.modele}</p>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,marginBottom:8}}>
                  {[['cableTire','Câble tiré'],['cableReutil','Câble réutil'],['apPose','AP posé']].map(([f,lbl]) => (
                    <div key={f}>
                      <p style={{fontSize:11,color:'#666',marginBottom:3}}>{lbl}</p>
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        {['Oui','Non','En cours'].map(v => (
                          <button key={v} onClick={()=>upd(i,f,v)}
                            style={{...styles.miniChip,...(item[f]===v?styles.miniChipOn:{})}}>{v}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                  <span style={{fontSize:12,color:'#666'}}>Controller pingé :</span>
                  <button style={{...styles.miniChip,...(item.controllerPing?styles.miniChipOn:{})}}
                    onClick={()=>upd(i,'controllerPing',!item.controllerPing)}>
                    {item.controllerPing ? '✅ Oui' : '⬜ Non'}
                  </button>
                </div>
                {/* Photos */}
                <div style={{marginBottom:8}}>
                  {needPhoto && <p style={{fontSize:12,color:'#EF6C00',marginBottom:6}}>⚠️ Photo requise (AP posé)</p>}
                  <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:8}}>
                    {(item.photos||[]).map((ph,pi) => (
                      <img key={pi} src={ph.path} alt="AP"
                        style={{...styles.thumb, ...(ph.pending?{opacity:.55, borderColor:'#FFC91F', borderStyle:'dashed'}:{})}}/>
                    ))}
                  </div>
                  <input type="file" accept="image/*" multiple capture="environment"
                    ref={el => fileRefs.current[i] = el}
                    style={{display:'none'}} onChange={e=>uploadPhoto(i,Array.from(e.target.files))}/>
                  <button style={styles.photoBtn} onClick={()=>fileRefs.current[i]?.click()}>
                    📷 Ajouter photo(s)
                  </button>
                </div>
                <textarea style={{...styles.textarea,fontSize:12}} rows={1}
                  placeholder="Observation..." value={item.observation||''}
                  onChange={e=>upd(i,'observation',e.target.value)}/>
              </div>
  );
}

function StepAP({ apItems, setApItems, apList, isDone, isTreated, weekProg, currentSemaine }) {
  const fileRefs = useRef({});
  const [openZones, setOpenZones] = useState({});

  function upd(i, field, val) {
    setApItems(a => a.map((r,j) => j===i ? {...r,[field]:val} : r));
  }

  async function uploadPhoto(i, files) {
    const apRef = apItems[i]?.apRef;
    for (const file of files) {
      if (navigator.onLine) {
        const fd = new FormData(); fd.append('photo', file);
        try {
          const { data } = await api.post('/reports/photo', fd, { headers:{'Content-Type':'multipart/form-data'} });
          setApItems(a => a.map((r,j) => j===i ? {...r, photos:[...(r.photos||[]), data]} : r));
          continue;
        } catch { /* bascule en file locale */ }
      }
      const qid = await queuePhoto(file, file.name, { kind:'ap', key: apRef });
      const local = { qid, path: URL.createObjectURL(file), pending: true };
      setApItems(a => a.map((r,j) => j===i ? {...r, photos:[...(r.photos||[]), local]} : r));
      setQueueCount(c => c + 1);
    }
  }

  const byZone = {};
  apItems.forEach((item,i) => {
    const z = item.zone || 'Autre';
    if (!byZone[z]) byZone[z] = [];
    byZone[z].push({...item, _idx:i});
  });
  // Tri par zone : non traités (ni câble ni pose) d'abord, puis traités, terminés en fin
  const score = it => isDone(it) ? 2 : isTreated(it) ? 1 : 0;
  for (const z of Object.keys(byZone))
    byZone[z].sort((a, b) => score(a) - score(b) || a.apRef.localeCompare(b.apRef));

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Points d'accès – {apItems.length} AP</h3>
      {weekProg && weekProg.total > 0 && (
        <div style={styles.weekProg}>
          <p style={{fontSize:12, fontWeight:700, color:'#13264D', marginBottom:5}}>
            Avancement {weekProg.semaine} :
            posés <b>{weekProg.pose}/{weekProg.total}</b>
            {weekProg.totalNew > 0 && <> · tirés <b>{weekProg.tire}/{weekProg.totalNew}</b></>}
            {weekProg.totalReutil > 0 && <> · réutil <b>{weekProg.reutil}/{weekProg.totalReutil}</b></>}
          </p>
          <div style={{height:7, background:'#DDE4F0', borderRadius:4, overflow:'hidden'}}>
            <div style={{height:'100%', width:`${Math.round(weekProg.pose/weekProg.total*100)}%`,
              background:'#2E7D32', borderRadius:4}}/>
          </div>
        </div>
      )}
      <p style={{fontSize:13,color:'#666',marginBottom:12}}>
        Mettre à jour l'avancement de chaque AP traité cette nuit.
        Photo obligatoire si AP posé = Oui. Les AP terminés (câble + posé + pingé)
        passent en fin de liste, marqués en vert.
      </p>
      {/* corps zones — repliables, non traités en premier */}
      {Object.entries(byZone).map(([zone, items]) => {
        const nbDone = items.filter(isDone).length;
        const allDone = nbDone === items.length;
        const opened = openZones[zone] ?? !allDone;
        return (
        <div key={zone}>
          <button style={{...styles.zoneHeader, width:'100%', display:'flex',
            justifyContent:'space-between', alignItems:'center', cursor:'pointer'}}
            onClick={() => setOpenZones(o => ({ ...o, [zone]: !opened }))}>
            <span>{opened ? '▾' : '▸'} {zone}</span>
            <span style={{fontSize:12, fontWeight:600,
              color: allDone ? '#B8E6BE' : '#FFC91F'}}>{nbDone}/{items.length} terminés</span>
          </button>
          {opened && items.map(item => (
            <ApCard key={item.apRef} item={item} i={item._idx} upd={upd}
              uploadPhoto={uploadPhoto} fileRefs={fileRefs} done={isDone(item)}
              currentSemaine={currentSemaine}/>
          ))}
        </div>
        );
      })}
    </div>
  );
}

// ── Étape 3 : Zones de travail ────────────────────────────────────────
function StepZones({ workZones, setWorkZones }) {
  const fileRefs = useRef({});

  function addZone() {
    setWorkZones(z => [...z, { description:'', travaux:'', heureDebut:'', heureFin:'', photos:[] }]);
  }
  function upd(i, field, val) {
    setWorkZones(z => z.map((r,j) => j===i ? {...r,[field]:val} : r));
  }
  function remove(i) { setWorkZones(z => z.filter((_,j) => j!==i)); }

  async function uploadPhoto(i, files) {
    for (const file of files) {
      if (navigator.onLine) {
        const fd = new FormData(); fd.append('photo', file);
        try {
          const { data } = await api.post('/reports/photo', fd, { headers:{'Content-Type':'multipart/form-data'} });
          setWorkZones(z => z.map((r,j) => j===i ? {...r, photos:[...r.photos, data]} : r));
          continue;
        } catch { /* file locale */ }
      }
      const qid = await window.__queuePhotoZone(file, i);
      const local = { qid, path: URL.createObjectURL(file), pending: true };
      setWorkZones(z => z.map((r,j) => j===i ? {...r, photos:[...r.photos, local]} : r));
    }
  }

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Zones de travail</h3>
      <p style={{fontSize:13,color:'#666',marginBottom:12}}>
        Documenter chaque zone où l'équipe a travaillé. Photos libres.
      </p>
      {workZones.map((zone, i) => (
        <div key={i} style={{...styles.card, borderLeft:'4px solid #2E75B6'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <span style={{fontWeight:700,color:'#2E75B6'}}>Zone {i+1}</span>
            <button style={styles.removeBtn} onClick={()=>remove(i)}>✕ Supprimer</button>
          </div>
          <Field label="Description de la zone *" value={zone.description}
            onChange={v=>upd(i,'description',v)} placeholder="Ex: Allée B-012 à B-018, côté est, nacelle N1"/>
          <div style={{height:8}}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,margin:'8px 0'}}>
            <Field label="Heure début" type="time" value={zone.heureDebut} onChange={v=>upd(i,'heureDebut',v)}/>
            <Field label="Heure fin"   type="time" value={zone.heureFin}   onChange={v=>upd(i,'heureFin',v)}/>
          </div>
          <p style={{fontSize:12,color:'#666',marginBottom:4}}>Travaux réalisés</p>
          <textarea style={styles.textarea} rows={2} value={zone.travaux}
            onChange={e=>upd(i,'travaux',e.target.value)}
            placeholder="Ex: Tirage 4 prises new + pose 3 AP. Brassage partiel."/>
          {/* Photos zone */}
          <div style={{marginTop:10}}>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:8}}>
              {zone.photos.map((ph,pi) => (
                <img key={pi} src={ph.path} alt=""
                  style={{...styles.thumb, ...(ph.pending?{opacity:.55, borderColor:'#FFC91F', borderStyle:'dashed'}:{})}}/>
              ))}
            </div>
            <input type="file" accept="image/*" multiple capture="environment"
              ref={el=>fileRefs.current[i]=el} style={{display:'none'}}
              onChange={e=>uploadPhoto(i,Array.from(e.target.files))}/>
            <button style={styles.photoBtn} onClick={()=>fileRefs.current[i]?.click()}>
              📷 Ajouter photos de la zone
            </button>
          </div>
        </div>
      ))}
      <button style={{...styles.btn, background:'#2E75B6', width:'100%', marginTop:8}}
        onClick={addZone}>+ Ajouter une zone de travail</button>
    </div>
  );
}

// ── Étape 4 : Matériaux ───────────────────────────────────────────────
function StepMateriaux({ materiaux, setMateriaux }) {
  function upd(i, field, val) {
    setMateriaux(m => m.map((r,j) => j===i ? {...r,[field]:val} : r));
  }
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Consommation matériel</h3>
      <p style={{fontSize:13,color:'#666',marginBottom:12}}>Saisir uniquement les quantités utilisées.</p>
      {materiaux.map((m, i) => (
        <div key={i} style={{display:'flex',alignItems:'center',gap:8,
          padding:'8px 12px',background:i%2?'#f9f9f9':'#fff',
          borderRadius:8,marginBottom:4,border:'1px solid #eee'}}>
          {m.isCustom
            ? <input style={{...styles.input,flex:3,padding:'6px 8px',fontSize:13}}
                placeholder="Désignation libre" value={m.designation}
                onChange={e=>upd(i,'designation',e.target.value)}/>
            : <span style={{flex:3,fontSize:13}}>{m.designation}</span>
          }
          <input style={{...styles.input,flex:1,padding:'6px 8px',fontSize:13,textAlign:'center'}}
            type="number" min="0" step="0.5" placeholder="0"
            value={m.quantite} onChange={e=>upd(i,'quantite',e.target.value)}/>
          <span style={{fontSize:12,color:'#888',minWidth:24}}>{m.unite}</span>
        </div>
      ))}
    </div>
  );
}

// ── Étape 5 : Incidents ───────────────────────────────────────────────
function StepIncidents({ incidents, setIncidents }) {
  function add() {
    setIncidents(i => [...i, { datetime:'', nature:'', detail:'', impact:'', actionCorrective:'' }]);
  }
  function upd(i, field, val) { setIncidents(a => a.map((r,j) => j===i?{...r,[field]:val}:r)); }
  function remove(i) { setIncidents(a => a.filter((_,j)=>j!==i)); }

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Incidents</h3>
      {incidents.length === 0 && <p style={{color:'#2E7D32',fontWeight:600}}>✅ Aucun incident</p>}
      {incidents.map((inc, i) => (
        <div key={i} style={{...styles.card, borderLeft:'4px solid #C62828'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
            <span style={{fontWeight:700,color:'#C62828'}}>Incident {i+1}</span>
            <button style={styles.removeBtn} onClick={()=>remove(i)}>✕</button>
          </div>
          <Field label="Date / Heure" type="datetime-local" value={inc.datetime} onChange={v=>upd(i,'datetime',v)}/>
          <div style={{height:8}}/>
          <p style={{fontSize:12,color:'#666',marginBottom:4}}>Nature</p>
          <Select value={inc.nature} onChange={v=>upd(i,'nature',v)} options={INCIDENTS_NATURE}/>
          <div style={{height:8}}/>
          <p style={{fontSize:12,color:'#666',marginBottom:4}}>Détail</p>
          <textarea style={styles.textarea} rows={3} value={inc.detail}
            onChange={e=>upd(i,'detail',e.target.value)} placeholder="Décrire l'incident..."/>
          <div style={{height:8}}/>
          <p style={{fontSize:12,color:'#666',marginBottom:4}}>Impact planning</p>
          <Select value={inc.impact} onChange={v=>upd(i,'impact',v)} options={IMPACTS}/>
          <div style={{height:8}}/>
          <p style={{fontSize:12,color:'#666',marginBottom:4}}>Action corrective</p>
          <textarea style={styles.textarea} rows={2} value={inc.actionCorrective}
            onChange={e=>upd(i,'actionCorrective',e.target.value)}/>
        </div>
      ))}
      <button style={{...styles.btn,background:'#C62828',width:'100%',marginTop:8}} onClick={add}>
        + Ajouter un incident
      </button>
    </div>
  );
}

// ── Étape 6 : Restants ────────────────────────────────────────────────
function StepRestants({ restants, setRestants }) {
  function add() { setRestants(r => [...r, { tache:'', quantite:'', priorite:'Haute', note:'' }]); }
  function upd(i, f, v) { setRestants(a => a.map((r,j) => j===i?{...r,[f]:v}:r)); }
  function remove(i) { setRestants(a => a.filter((_,j)=>j!==i)); }
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Travaux restants</h3>

      {restants.map((r, i) => (
        <div key={i} style={{...styles.card,borderLeft:'4px solid #EF6C00'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
            <span style={{fontWeight:600}}>Restant {i+1}</span>
            <button style={styles.removeBtn} onClick={()=>remove(i)}>✕</button>
          </div>
          <Field label="Tâche" value={r.tache} onChange={v=>upd(i,'tache',v)}
            placeholder="Ex: 3 prises C1 non tirées"/>
          <div style={{height:8}}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <Field label="Quantité restante" value={r.quantite} onChange={v=>upd(i,'quantite',v)}
              placeholder="Ex: 3 prises"/>
            <div>
              <p style={{fontSize:12,color:'#666',marginBottom:4}}>Priorité J+1</p>
              <Select value={r.priorite} onChange={v=>upd(i,'priorite',v)} options={PRIORITES}/>
            </div>
          </div>
          <div style={{height:8}}/>
          <Field label="Note" value={r.note} onChange={v=>upd(i,'note',v)} placeholder="Remarque"/>
        </div>
      ))}
      <button style={{...styles.btn,background:'#EF6C00',width:'100%',marginTop:8}} onClick={add}>
        + Ajouter un restant
      </button>
    </div>
  );
}

// ── Étape 7 : Validation ──────────────────────────────────────────────
function StepValidation({ header, apItems, presences, incidents, restants, workZones, signature, setSignature, signataire, setSignataire }) {
  const apPoses  = apItems.filter(a => a.apPose === 'Oui').length;
  const apTires  = apItems.filter(a => a.cableTire === 'Oui').length;
  const presents = presences.filter(p => p.present).length;
  const noPhoto  = apItems.filter(a => a.apPose==='Oui' && (!a.photos||a.photos.length===0));
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Récapitulatif avant soumission</h3>
      <div style={styles.card}>
        <p style={styles.cardLabel}>Nuit</p>
        <p style={{fontWeight:600}}>{header.numJour} {header.jour} – {header.semaine}</p>
        <p style={{color:'#666',fontSize:13}}>{header.date}</p>
      </div>
      <div style={styles.card}>
        <p style={styles.cardLabel}>Résumé</p>
        <p>👥 {presents} techniciens présents</p>
        <p>🔧 {apTires} câbles tirés</p>
        <p>📡 {apPoses} AP posés</p>
        <p>🗺️ {workZones.length} zones documentées</p>
        <p>⚠️ {incidents.length} incident(s)</p>
        <p>📋 {restants.filter(r=>r.tache).length} restant(s)</p>
      </div>
      {noPhoto.length > 0 && (
        <div style={{...styles.card,background:'#fff9f0',borderLeft:'4px solid #EF6C00'}}>
          <p style={{color:'#EF6C00',fontWeight:600}}>⚠️ Photos manquantes</p>
          {noPhoto.map(a => <p key={a.apRef} style={{fontSize:13}}>{a.apRef} – AP posé sans photo</p>)}
        </div>
      )}
      <div style={styles.card}>
        <p style={styles.cardLabel}>Validation client (facultatif)</p>
        <input style={{...styles.input, marginBottom:8}} placeholder="Nom du signataire"
          value={signataire} onChange={e=>setSignataire(e.target.value)}/>
        <SignaturePad onChange={setSignature}/>
      </div>
      <div style={{...styles.card,background:'#e8f5e9'}}>
        <p style={{color:'#2E7D32',fontWeight:600}}>Prêt à soumettre</p>
        <p style={{fontSize:13,color:'#555'}}>Le rapport sera envoyé au bureau dès soumission.</p>
      </div>
    </div>
  );
}

// ── Helpers UI ────────────────────────────────────────────────────────
function Row({ label, children }) {
  return (
    <div style={{marginBottom:12}}>
      <p style={{fontSize:13,color:'#555',marginBottom:4}}>{label}</p>
      {children}
    </div>
  );
}
function Field({ label, type='text', value, onChange, placeholder='' }) {
  return (
    <div>
      {label && <p style={{fontSize:12,color:'#666',marginBottom:3}}>{label}</p>}
      <input style={styles.input} type={type} value={value} placeholder={placeholder}
        onChange={e=>onChange(e.target.value)}/>
    </div>
  );
}
function Select({ value, onChange, options }) {
  return (
    <select style={{...styles.input,background:'#fff'}} value={value} onChange={e=>onChange(e.target.value)}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const styles = {
  page:{ maxWidth:600, margin:'0 auto', minHeight:'100vh', display:'flex', flexDirection:'column',
         paddingBottom:120 },
  topbar:{ color:'#fff', display:'flex', alignItems:'center',
           justifyContent:'space-between', padding:'12px 16px', position:'sticky', top:0, zIndex:10 },
  topbarTitle:{ fontWeight:700, fontSize:15 },
  badge:{ fontSize:11, padding:'3px 8px', borderRadius:20, fontWeight:600 },
  topBtn:{ background:'rgba(255,255,255,.18)', border:'none', color:'#fff', padding:'6px 10px',
           borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' },
  stepsWrap:{ display:'flex', gap:4, padding:'12px 16px 6px', background:'#fff' },
  stepSeg:{ flex:1, height:8, borderRadius:4, border:'none', cursor:'pointer', padding:0 },
  stepLabel:{ textAlign:'center', fontSize:13, fontWeight:700, color:'#13264D',
              padding:'0 0 10px', background:'#fff', borderBottom:'1px solid #DDE4F0' },
  content:{ flex:1, padding:'12px 16px', overflowY:'auto' },
  nav:{ position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)', width:'100%', maxWidth:600,
        background:'#fff', borderTop:'1px solid #eee', display:'flex', gap:8, padding:'12px 16px' },
  btn:{ flex:1, background:'#1E3A6E', color:'#fff', borderRadius:10, padding:'12px 8px',
        fontSize:14, fontWeight:600 },
  section:{ paddingBottom:16 },
  sectionTitle:{ fontSize:17, fontWeight:800, color:'#13264D', marginBottom:12 },
  card:{ background:'#fff', borderRadius:14, padding:14, marginBottom:10,
         boxShadow:'0 1px 2px rgba(19,38,77,.06), 0 4px 14px rgba(19,38,77,.05)', border:'1px solid #DDE4F0' },
  cardLabel:{ fontSize:12, color:'#888', fontWeight:600, marginBottom:6, textTransform:'uppercase' },
  apCard:{ background:'#fff', borderRadius:12, padding:12, marginBottom:8,
           boxShadow:'0 1px 4px rgba(0,0,0,.06)' },
  apHeader:{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 },
  typeBadge:{ color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20 },
  zoneHeader:{ background:'#E3F2FD', borderRadius:8, padding:'8px 12px', marginBottom:6,
               fontWeight:700, color:'#1565C0', fontSize:13 },
  chip:{ border:'1.5px solid #ccc', borderRadius:20, padding:'5px 12px', fontSize:12,
         background:'#fff', color:'#555', fontWeight:500 },
  chipOn:{ background:'#1E3A6E', color:'#fff', borderColor:'#1E3A6E' },
  miniChip:{ border:'1px solid #ddd', borderRadius:6, padding:'3px 8px', fontSize:11,
             background:'#fff', color:'#555' },
  miniChipOn:{ background:'#2E7D32', color:'#fff', borderColor:'#2E7D32' },
  photoBtn:{ background:'#f0f4f8', border:'1px dashed #2E75B6', borderRadius:8,
             padding:'8px 14px', color:'#2E75B6', fontSize:13, fontWeight:600, width:'100%' },
  thumb:{ width:72, height:72, objectFit:'cover', borderRadius:8, border:'1px solid #ddd' },
  removeBtn:{ background:'#fee', color:'#C62828', border:'1px solid #fcc', borderRadius:6,
              padding:'4px 10px', fontSize:12, fontWeight:600 },
  input:{ width:'100%', border:'1.5px solid #e0e0e0', borderRadius:8, padding:'10px 12px',
          fontSize:15, outline:'none', background:'#fff' },
  textarea:{ width:'100%', border:'1.5px solid #e0e0e0', borderRadius:8, padding:'10px 12px',
             fontSize:14, outline:'none', resize:'vertical', fontFamily:'inherit' },
  center:{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
           minHeight:'100vh', padding:24, textAlign:'center' },
  rejectBanner:{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10,
                 background:'#FDECEA', borderLeft:'4px solid #C62828', color:'#7A1F1B',
                 padding:'12px 16px', margin:'10px 16px 0', borderRadius:10 },
  rejectBtn:{ background:'#C62828', color:'#fff', borderRadius:8, padding:'8px 14px',
              fontSize:13, fontWeight:700, whiteSpace:'nowrap' },
  queueBanner:{ background:'#FFFBEC', borderLeft:'4px solid #FFC91F', color:'#8A6D00',
                padding:'8px 16px', margin:'8px 16px 0', borderRadius:10, fontSize:13, fontWeight:600 },
  weekProg:{ background:'#fff', border:'1px solid #DDE4F0', borderRadius:12,
             padding:'10px 12px', marginBottom:10 },
  planBanner:{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10,
               background:'#EAF1FB', borderLeft:'4px solid #2E75B6', color:'#1E3A6E',
               padding:'12px 16px', margin:'10px 16px 0', borderRadius:10 },
  planBannerBtn:{ background:'#2E75B6', color:'#fff', borderRadius:8, padding:'8px 14px',
                  fontSize:13, fontWeight:700, whiteSpace:'nowrap' },
  modalWrap:{ position:'fixed', inset:0, background:'rgba(19,38,77,.6)', zIndex:50,
              display:'flex', alignItems:'center', justifyContent:'center', padding:12 },
  modal:{ background:'#fff', borderRadius:14, padding:14, width:'100%', maxWidth:900,
          maxHeight:'92vh', overflowY:'auto' },
  topBtn2:{ background:'#1E3A6E', color:'#fff', padding:'6px 12px', borderRadius:8,
            fontSize:12, fontWeight:600 },
};
